import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import Container from '@components/ContentContainer';
import { ContactHeader } from '@repo/components/headers';
import {
  ContactBio,
  ContactEducation,
  ContactExperience,
  ContactInterests,
  ContactSocialProfiles,
  ContactWebsites,
} from '@repo/components/contact';

import { NotesList } from '@components/Contact/Notes';
import { SharedContactHighlightsList } from '@components/Contact/Highlights';
import { useParams } from 'react-router-dom';
import { useAutoFocus, useScrollingClass, useAuthContext } from '@repo/hooks';
import { selectSharedContact } from '@repo/redux/reducers/sharedData/contacts';
import { fetchSharedContact } from '@repo/redux/actions/shared/contacts';
import { fetchSharedContactNotes } from '@repo/redux/actions/shared/notes';
import { selectSharedContactNotes } from '@repo/redux/reducers/sharedData/notes';
import { fetchSharedContactHighlights } from '@repo/redux/actions/shared/highlights';
import { selectSharedContactHighlights } from '@repo/redux/reducers/sharedData/highlights';
import styles from './SharedContact.module.scss';
import { fetchSharedContactMoments } from '@repo/redux/actions/shared/moments';
import { selectSharedContactInteractionSummary } from '@repo/redux/reducers/sharedData/moments';
import { fetchSharedContactSources } from '@repo/redux/actions/shared/sources';
import { selectSharedContactSources } from '@repo/redux/reducers/sharedData/sources';
import { ContactViewSidebarDisplay } from '@components/Contact/Sidebar';
import SharedViewWrapper from '@components/Sharing/SharedViewWrapper';
import SharedResourceUtilityBar from '@components/Sharing/SharedResourceUtilityBar';

export default function SharedContact() {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { uuid: contactUUID } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayLogIn, setDisplayLogIn] = useState(false);
  const contact = useSelector(selectSharedContact(contactUUID));
  const notes = useSelector(selectSharedContactNotes(contactUUID));
  const highlights = useSelector(selectSharedContactHighlights(contactUUID));
  const summary = useSelector(selectSharedContactInteractionSummary(contactUUID));
  const sources = useSelector(selectSharedContactSources(contactUUID));

  useEffect(() => {
    dispatch(fetchSharedContact(contactUUID, user));
    dispatch(fetchSharedContactNotes(contactUUID, user));
    dispatch(fetchSharedContactHighlights(contactUUID, user));
    dispatch(fetchSharedContactMoments(contactUUID, user));
    dispatch(fetchSharedContactSources(contactUUID, user));
  }, [contactUUID]);

  useScrollingClass(containerRef, 'contact-scrolled');
  useAutoFocus(containerRef);

  return (
    <SharedViewWrapper
      sharedResourceType="contact"
      displayLogIn={displayLogIn}
      error={contact.error}
    >
      <Container key={contactUUID}>
        <Helmet title={contact.displayName} />
        <Container.Primary
          ref={containerRef}
          className={styles.container}
          header={
            <SharedResourceUtilityBar
              sharedResource={contact}
              sharedResourceType="contact"
              logIn={() => setDisplayLogIn(true)}
            >
              <span className={styles.contactName}>{contact.displayName}</span>
            </SharedResourceUtilityBar>
          }
        >
          <div className={styles.content}>
            <ContactHeader contact={contact} />
            <NotesList notes={notes} readOnly />
            <ContactBio contact={contact} />
            <ContactSocialProfiles contact={contact} />
            <SharedContactHighlightsList highlights={highlights} />
            <ContactExperience contact={contact} />
            <ContactEducation contact={contact} />
            <ContactWebsites contact={contact} />
            <ContactInterests contact={contact} clickable={!!user} />
          </div>
        </Container.Primary>
        <ContactViewSidebarDisplay
          className={!user && styles.unauthenticatedUser}
          contact={contact}
          sources={sources}
          summary={summary}
        />
      </Container>
    </SharedViewWrapper>
  );
}
